import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { notification } from 'antd';

import DeviceEndOfSupportContext, {
  Filters,
} from '@totem/components/deviceEndOfSupport/deviceEndOfSupportContext';
import {
  EndOfSupportCoverage,
  EndOfSupportRow,
  SortState,
} from '@totem/components/deviceEndOfSupport/types';
import {
  defaultOrder,
  FILTER_VALUE,
  FilterKey,
  STATUS,
  toRow,
} from '@totem/components/deviceEndOfSupport/utilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { DeviceQueryResults } from '@totem/types/devices';
import { DEVICES_PAGINATION_ENDPOINT } from '@totem/utilities/endpoints';

const SORTERS: Record<
  string,
  (left: EndOfSupportRow, right: EndOfSupportRow) => number
> = {
  name: (left, right) =>
    left.name.toLowerCase().localeCompare(right.name.toLowerCase()),
  status: (left, right) => STATUS[left.status].rank - STATUS[right.status].rank,
  eos: (left, right) => (left.eos ?? '9999').localeCompare(right.eos ?? '9999'),
  eol: (left, right) => (left.eol ?? '9999').localeCompare(right.eol ?? '9999'),
  building: (left, right) => left.building.localeCompare(right.building),
};

type Props = {
  children: ReactNode;
};

// Loads every End of Support device for the current org once; the report filters, sorts and
// groups them in the browser so the KPI tiles, building view and data notes all agree.
const DeviceEndOfSupportContainer = ({ children }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);

  const [loading, setLoading] = useState<boolean>(true);
  const [rows, setRows] = useState<EndOfSupportRow[]>([]);
  const [coverage, setCoverage] = useState<EndOfSupportCoverage | null>(null);
  const [filters, setFilters] = useState<Filters>({});
  const [search, setSearch] = useState<string>('');
  const [sort, setSort] = useState<SortState>({ key: null, order: null });
  const [activeTab, setActiveTab] = useState<string>('devices');

  const refresh = useCallback(() => {
    setLoading(true);
    const headers = { Authorization: `Bearer ${getApiToken()}` };

    const devices = fetch(`${DEVICES_PAGINATION_ENDPOINT}/endOfSupport`, {
      method: 'POST',
      headers,
      body: JSON.stringify({}),
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then((result: DeviceQueryResults) =>
        setRows((result.devices ?? []).map(toRow).sort(defaultOrder)),
      )
      .catch(() =>
        notification.error({
          message: 'Failed to load End of Support devices.',
        }),
      );

    // Coverage only adds context; the report still works without it.
    const counts = fetch(
      `${DEVICES_PAGINATION_ENDPOINT}/endOfSupport/coverage`,
      { headers },
    )
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then((result: EndOfSupportCoverage) => setCoverage(result))
      .catch(() => setCoverage(null));

    Promise.all([devices, counts]).finally(() => setLoading(false));
  }, [getApiToken]);

  // The report always targets the active organization.
  useEffect(() => {
    setFilters({});

    setSearch('');

    refresh();
  }, [userProfile.organization.id, refresh]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const matched = rows.filter(
      (row) =>
        (Object.keys(filters) as FilterKey[]).every(
          (key) =>
            !filters[key]?.length ||
            filters[key].includes(FILTER_VALUE[key](row)),
        ) &&
        (!term ||
          [row.name, row.type, row.vendor]
            .join(' ')
            .toLowerCase()
            .includes(term)),
    );
    const sorter = sort.key && sort.order ? SORTERS[sort.key] : null;
    if (!sorter) {
      return matched;
    }
    const direction = sort.order === 'descend' ? -1 : 1;
    return [...matched].sort(
      (left, right) =>
        sorter(left, right) * direction || defaultOrder(left, right),
    );
  }, [rows, filters, search, sort]);

  const setFilter = (key: FilterKey, values: string[]) =>
    setFilters((current) => ({ ...current, [key]: values }));

  const resetAll = () => {
    setFilters({});

    setSearch('');
  };

  return (
    <DeviceEndOfSupportContext.Provider
      value={{
        loading,
        rows,
        filteredRows,
        coverage,
        filters,
        setFilter,
        search,
        setSearch,
        sort,
        setSort,
        resetAll,
        activeTab,
        setActiveTab,
        onRefresh: refresh,
      }}
    >
      {children}
    </DeviceEndOfSupportContext.Provider>
  );
};

export default DeviceEndOfSupportContainer;
